import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonRow,
  IonTextarea,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IDecaDocument, IDecaDocumentSegment, isDecaServicePriorToTimestamp, UUID } from '@okcargo-apps/shared-domain';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaEditGoodsForm, DecaEditReasonFormModel } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-goods-description-modal',
  templateUrl: './deca-edit-goods-description-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonTextarea,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    DecaEditReasonComponent,
    DecaListItemComponent,
  ],
})
export class DecaEditGoodsDescriptionModalComponent implements OnInit {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;
  public readonly segment!: IDecaDocumentSegment;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);

  protected readonly form: FormGroup<DecaEditGoodsForm> = this.fb.group<DecaEditGoodsForm>({
    goodsDescription: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(250)]),
    changeReason: this.fb.group<DecaEditReasonFormModel>({ reason: this.fb.nonNullable.control('') }),
  });

  protected get isServiceStarted(): boolean {
    return isDecaServicePriorToTimestamp(this.document, new Date());
  }

  private requireChangeReasonWhenServiceStarted(): void {
    if (!this.isServiceStarted) return;

    const reason = this.form.controls.changeReason.controls.reason;
    reason.addValidators(Validators.required);
    reason.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.requireChangeReasonWhenServiceStarted();
    this.form.patchValue({
      goodsDescription: this.segment.goodsDescription ?? '',
    });
  }

  protected get errorText(): string {
    const control = this.form.controls.goodsDescription;
    if (control.hasError('required')) return 'Debe añadir texto para definir la mercancía';
    if (control.hasError('maxlength')) return 'La descripción no puede superar los 250 caracteres';
    return 'Dato no válido';
  }

  protected async saveChanges(): Promise<void> {
    const rawText = this.form.controls.goodsDescription.value.trim();

    if (this.form.invalid || !rawText) {
      if (!rawText) this.form.controls.goodsDescription.setValue('');
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const updateSegment: IDecaDocumentSegment = {
        ...this.segment,
        goodsDescription: rawText,
      };

      const updatedDocument: IDecaDocument = {
        ...this.document,
        segments: this.document.segments.map((segment) => (segment.id === updateSegment.id ? updateSegment : segment)),
      };

      await this.decaMutationsService.updateGoodsDescription(
        updatedDocument,
        this.segment.id,
        this.form.controls.changeReason.controls.reason.value.trim(),
      );

      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: ['Ha enviado una nueva descripción de mercancía a este documento'],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error updating goods description', error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
